package com.example.mystybag.common.ui.components.custom

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import com.example.mystybag.core.views.AppTheme
import dev.icerock.moko.resources.compose.painterResource

@Composable
fun <T> ComboBox(
    modifier: Modifier = Modifier,
    value: T?,
    items: List<T>,
    onValueChange: (T) -> Unit,
    label: String,
    enabled: Boolean = true,
    displayText: (T) -> String,
    isError: Boolean = false,
    errorText: String = "",
    useRightArrow: Boolean = true,
    backgroundColor: Color? = null,
    borderColor: Color? = null,
    /** Figma: disabled select shows value in onSurface, label in onSurfaceVariant. */
    disabledTextColor: Color? = null,
    trailingIconSize: Dp = p16
) {
    var expanded by remember { mutableStateOf(false) }
    val displayValue = value?.let { displayText(it) } ?: ""
    val hasSelection = value != null
    // Right arrow by default: gray when not selected, green when selected (per design)
    val arrowIcon = when {
        useRightArrow -> if (enabled && hasSelection) SRC.images.combobox_arrow_right_green else SRC.images.combobox_arrow_right_gray
        else -> if (enabled && hasSelection) SRC.images.combobox_arrow_down_green else SRC.images.combobox_arrow_down_gray
    }

    Box(modifier = modifier) {
        DefaultCircleShapedTextField(
            modifier = Modifier.fillMaxWidth(),
            value = displayValue,
            onValueChange = {},
            label = label,
            enabled = enabled,
            readOnly = true,
            canFocus = false,
            isError = isError,
            errorText = errorText,
            backgroundColor = backgroundColor,
            borderColor = borderColor,
            disabledTextColor = disabledTextColor,
            trailingIcon = {
                Image(
                    painter = painterResource(arrowIcon),
                    modifier = Modifier.size(trailingIconSize)
                )
            }
        )

        // Overlay clickable area on top of the text field
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(p56)
                .clickable(enabled = enabled && items.isNotEmpty()) {
                    if (items.isNotEmpty()) {
                        expanded = true
                    }
                }
        )

        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            containerColor = AppTheme.current.colors.surface,
            shape = RoundedCornerShape(p20),
            offset = DpOffset(x = 0.dp, y = 4.dp),
            modifier = Modifier.heightIn(max = 320.dp)
        ) {
            items.forEachIndexed { index, item ->
                DropdownMenuItem(
                    text = { Text(displayText(item)) },
                    onClick = {
                        onValueChange(item)
                        expanded = false
                    }
                )
                if (index < items.lastIndex) {
                    HorizontalDivider(
                        thickness = 1.dp,
                        color = AppTheme.current.colors.outline
                    )
                }
            }
        }
    }
}
